/**
 * 多言語対応の文字列定数
 * 日本語と英語のテキストを定義
 */

export type Language = 'ja' | 'en';

export interface I18nStrings {
  home: {
    tagLabel: string;
    articlesFound: (count: number) => string;
    articlesWithTag: (tag: string, count: number) => string;
    totalArticles: (count: number) => string;
    loading: string;
    fetchError: string;
    noArticlesWithTag: (tag: string) => string;
    noArticles: string;
    prevPage: string;
    nextPage: string;
    tagListTitle: string;
    tagListSubtitle: string;
    tagListSwipeable: string;
    tagCount: (n: number) => string;
    tagListError: string;
    tagListEmpty: string;
  };
  common: {
    loading: string;
  };
  postDetail: {
    slugNotSpecified: string;
    fetchError: string;
    errorOccurred: string;
    slugLabel: string;
    postNotFound: string;
    postNotFoundDescription: string;
    postNotPublished: string;
    publishedDate: string;
    updatedDate: string;
    headingLink: (text: string) => string;
    filterByTag: (tag: string) => string;
    tableOfContents: string;
    expandToc: string;
    collapseToc: string;
    dateFormat: (year: number, month: string, day: string, hour: string, minute: string) => string;
    dateOnlyFormat: (year: number, month: string, day: string) => string;
    shareOnX: string;
    shareOnBluesky: string;
    shareOnReddit: string;
    copyUrl: string;
    urlCopied: string;
    buyMeACoffee: string;
    relatedArticles: string;
  };
  notificationBanner: {
    viewDetails: string;
    close: string;
  };
  about: {
    development: {
      title: string;
    };
    architecture: {
      title: string;
      versionSelect: string;
      noDiagram: string;
      loadingDiagram: string;
    };
    other: {
      title: string;
    };
  };
  languageSwitcher: {
    switchToJapanese: string;
    switchToEnglish: string;
  };
  languageNotice: {
    japaneseAvailable: string;
    englishAvailable: string;
    switchToJapanese: string;
    switchToEnglish: string;
    close: string;
  };
}

const i18nStrings: Record<Language, I18nStrings> = {
  ja: {
    home: {
      tagLabel: 'タグ',
      articlesFound: (count: number) => `${count}件の記事が見つかりました`,
      articlesWithTag: (tag: string, count: number) => `「${tag}」タグの記事が${count}件見つかりました`,
      totalArticles: (count: number) => `全${count}件の記事`,
      loading: '読み込み中...',
      fetchError: '記事の取得に失敗しました',
      noArticlesWithTag: (tag: string) => `「${tag}」タグの記事がありません`,
      noArticles: '記事がありません',
      prevPage: '前へ',
      nextPage: '次へ',
      tagListTitle: 'タグ一覧',
      tagListSubtitle: '記事数でランキング',
      tagListSwipeable: 'スワイプ可',
      tagCount: (n: number) => `${n}件`,
      tagListError: 'タグの取得に失敗しました',
      tagListEmpty: 'タグがありません',
    },
    common: {
      loading: '読み込み中...',
    },
    postDetail: {
      slugNotSpecified: 'スラッグが指定されていません',
      fetchError: '記事の取得に失敗しました',
      errorOccurred: 'エラーが発生しました',
      slugLabel: 'スラッグ: ',
      postNotFound: '記事が見つかりません',
      postNotFoundDescription: 'お探しの記事は存在しないか、削除された可能性があります。',
      postNotPublished: 'この記事は公開されていません',
      publishedDate: '公開日: ',
      updatedDate: '更新日: ',
      headingLink: (text: string) => `${text}へのリンク`,
      filterByTag: (tag: string) => `${tag}タグでフィルター`,
      tableOfContents: '目次',
      expandToc: '目次を展開',
      collapseToc: '目次を折りたたむ',
      dateFormat: (year: number, month: string, day: string, hour: string, minute: string) => `${year}年${month}月${day}日 ${hour}:${minute}`,
      dateOnlyFormat: (year: number, month: string, day: string) => `${year}年${month}月${day}日`,
      shareOnX: 'Xで共有',
      shareOnBluesky: 'Blueskyで共有',
      shareOnReddit: 'Redditで共有',
      copyUrl: 'URLをコピー',
      urlCopied: 'URLをコピーしました',
      buyMeACoffee: '投げ銭する',
      relatedArticles: '関連記事',
    },
    notificationBanner: {
      viewDetails: '詳細を見る',
      close: '閉じる',
    },
    about: {
      development: {
        title: '開発環境',
      },
      architecture: {
        title: 'システム構成図',
        versionSelect: 'バージョンを選択',
        noDiagram: 'システム構成図が見つかりません',
        loadingDiagram: 'システム構成図を読み込み中...',
      },
      other: {
        title: '概要',
      },
    },
    languageSwitcher: {
      switchToJapanese: '日本語',
      switchToEnglish: 'English',
    },
    languageNotice: {
      japaneseAvailable: '日本語ページがあります',
      englishAvailable: 'English page is available',
      switchToJapanese: '日本語に切り替え',
      switchToEnglish: 'Switch to English',
      close: '閉じる',
    },
  },
  en: {
    home: {
      tagLabel: 'Tag',
      articlesFound: (count: number) => `${count} posts found`,
      articlesWithTag: (tag: string, count: number) => `${count} posts found with tag "${tag}"`,
      totalArticles: (count: number) => `Total ${count} posts`,
      loading: 'Loading...',
      fetchError: 'Failed to fetch posts',
      noArticlesWithTag: (tag: string) => `No posts found with tag "${tag}"`,
      noArticles: 'No posts',
      prevPage: 'Previous',
      nextPage: 'Next',
      tagListTitle: 'Tags',
      tagListSubtitle: 'Ranked by post count',
      tagListSwipeable: 'Swipeable',
      tagCount: (n: number) => `${n} posts`,
      tagListError: 'Failed to load tags',
      tagListEmpty: 'No tags',
    },
    common: {
      loading: 'Loading...',
    },
    postDetail: {
      slugNotSpecified: 'Slug is not specified',
      fetchError: 'Failed to fetch post',
      errorOccurred: 'An error occurred',
      slugLabel: 'Slug: ',
      postNotFound: 'Post not found',
      postNotFoundDescription: 'The post you are looking for may not exist or may have been deleted.',
      postNotPublished: 'This post is not published',
      publishedDate: 'Published: ',
      updatedDate: 'Updated: ',
      headingLink: (text: string) => `Link to ${text}`,
      filterByTag: (tag: string) => `Filter by ${tag} tag`,
      tableOfContents: 'Table of Contents',
      expandToc: 'Expand table of contents',
      collapseToc: 'Collapse table of contents',
      dateFormat: (year: number, month: string, day: string, hour: string, minute: string) => `${month}/${day}/${year} ${hour}:${minute}`,
      dateOnlyFormat: (year: number, month: string, day: string) => `${month}/${day}/${year}`,
      shareOnX: 'Share on X',
      shareOnBluesky: 'Share on Bluesky',
      shareOnReddit: 'Share on Reddit',
      copyUrl: 'Copy URL',
      urlCopied: 'URL copied',
      buyMeACoffee: 'Buy me a coffee',
      relatedArticles: 'Related Articles',
    },
    notificationBanner: {
      viewDetails: 'View Details',
      close: 'Close',
    },
    about: {
      development: {
        title: 'Development Environment',
      },
      architecture: {
        title: 'System Architecture',
        versionSelect: 'Select Version',
        noDiagram: 'System architecture diagram not found',
        loadingDiagram: 'Loading system architecture diagram...',
      },
      other: {
        title: 'Overview',
      },
    },
    languageSwitcher: {
      switchToJapanese: '日本語',
      switchToEnglish: 'English',
    },
    languageNotice: {
      japaneseAvailable: '日本語ページがあります',
      englishAvailable: 'English page is available',
      switchToJapanese: '日本語に切り替え',
      switchToEnglish: 'Switch to English',
      close: 'Close',
    },
  },
};

/**
 * 言語に応じた文字列を取得する
 * @param lang 言語コード ('ja' | 'en')
 * @returns 該当言語の文字列オブジェクト
 */
export const getI18nStrings = (lang: Language): I18nStrings => {
  return i18nStrings[lang] || i18nStrings.en;
};

